/**
 * Event Type Constants - SINGLE SOURCE OF TRUTH
 * 
 * All event type definitions should be imported from this file.
 * Do NOT duplicate these definitions elsewhere!
 * 
 * NOTE: Main Schedule vs. Camps/Programs Separation
 * 
 * These event types cover the main schedule system:
 * - Team coordination: practices, meetings, competitions
 * - Classes: tumbling, recreational, etc. (from class_schedules)
 * - Private lessons: one-on-one coaching sessions (from private_lesson_bookings)
 *
 * Note: clinics, camps, workshops, and open gyms are now session-based Classes
 * (see featureGuide entry `clinics_camps_workshops`). The standalone
 * `camps_events` program type was removed on 2026-04-26.
 */

// Local BadgeColor union — Badge.tsx itself doesn't export this type, so we
// keep the source of truth alongside the event-type mapping that uses it.
// Keep in sync with the `color` prop union in src/components/ui/Badge.tsx.
export type BadgeColor =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark'
  | 'purple'
  | 'blue'
  | 'orange'
  // Gym Closings V1 (plan 2026-05-21-004, U4): neutral-gray bucket for the
  // "gym closed" calendar overlay. Reused on mobile (U5) so the closed-day
  // band stays visually consistent across web and native. Distinct from
  // 'light' so a future feature can theme closings without affecting the
  // generic gray-pill default.
  | 'gym_closed';

// =============================================================================
// EVENT TYPE OPTIONS
// =============================================================================

/**
 * Event types for dropdown menus (singular labels)
 * Use this for: Create Event form, Edit Event form
 * 
 * Note: "competition" is excluded from creation dropdowns because
 * competitions are managed via the Comp Events module
 */
export const EVENT_TYPES = [
  { value: "practice", label: "Team Practice" },
  { value: "team_bonding", label: "Team Bonding" },
  { value: "fundraiser", label: "Fundraiser" },
  { value: "meeting", label: "Team Meeting" },
  { value: "parent_meeting", label: "Parent Meeting" },
  { value: "other", label: "Other Event" }
] as const;

/**
 * Event types for filter dropdowns (plural labels + "All" option)
 * Use this for: Schedule filters, Calendar filters
 */
export const EVENT_TYPES_FOR_FILTER = [
  { value: "all", label: "All Types" },
  { value: "practice", label: "Team Practices" },
  { value: "team_bonding", label: "Team Bonding" },
  { value: "fundraiser", label: "Fundraisers" },
  { value: "meeting", label: "Team Meetings" },
  { value: "parent_meeting", label: "Parent Meetings" },
  { value: "competition", label: "Competitions" },
  { value: "class", label: "Classes" },
  { value: "private_lesson", label: "Private Lessons" },
  { value: "tryout", label: "Tryouts / Evaluations" },
  { value: "other", label: "Other Events" }
] as const;

/**
 * All event types including competition (for display/reading purposes)
 */
export const ALL_EVENT_TYPES = [
  { value: "practice", label: "Team Practice" },
  { value: "team_bonding", label: "Team Bonding" },
  { value: "fundraiser", label: "Fundraiser" },
  { value: "meeting", label: "Team Meeting" },
  { value: "parent_meeting", label: "Parent Meeting" },
  { value: "competition", label: "Competition" },
  { value: "class", label: "Class" },
  { value: "private_lesson", label: "Private Lesson" },
  { value: "tryout", label: "Tryout / Evaluation" },
  { value: "other", label: "Other Event" }
] as const;

/**
 * The event_type values a gym can actually create from the Create/Edit Event form.
 * Derived from EVENT_TYPES so adding a type there picks it up everywhere else.
 */
export const CREATABLE_EVENT_TYPE_VALUES: readonly string[] = EVENT_TYPES.map((t) => t.value);

/** The event_type values a gym can create, as a union type. */
export type CreatableEventType = (typeof EVENT_TYPES)[number]["value"];

/**
 * Older event_type values that the Create Event form no longer offers but that
 * still exist on rows in the database. Reads must keep including them so legacy
 * events stay visible; writes should not offer them.
 *
 * "camp", "clinic" and "event" are live in production today. "social" and
 * "workshop" come from the older type union in utils/types/event and appear in
 * seed data; they are listed so such a row is shown rather than silently
 * dropped.
 */
export const LEGACY_EVENT_TYPE_VALUES: readonly string[] = [
  "camp",
  "clinic",
  "event",
  "social",
  "workshop",
];

/**
 * The event_type values a read filter should ask for: everything a gym can
 * create today, plus the known legacy values above.
 *
 * This is NOT a guarantee about the column — `events.event_type` is plain text
 * with no CHECK constraint, so an unlisted value is possible. Two known
 * consequences: a value outside this list is not returned, and because
 * PostgREST `.in()` excludes NULL, a row with no event_type is not returned
 * either (there are none in production today).
 */
export const READABLE_EVENT_TYPE_VALUES: readonly string[] = [
  ...CREATABLE_EVENT_TYPE_VALUES,
  ...LEGACY_EVENT_TYPE_VALUES,
];

/**
 * The non-practice subset, for callers that let a user ask for practices and
 * other events separately.
 */
export const NON_PRACTICE_EVENT_TYPE_VALUES: readonly string[] = READABLE_EVENT_TYPE_VALUES.filter(
  (value) => value !== "practice"
);

// =============================================================================
// EVENT TYPE NAME MAPPING
// =============================================================================

/**
 * Event type value to label mapping
 * Use this when you need to get the display label from a value
 */
export const EVENT_TYPE_NAMES: Record<string, string> = {
  "practice": "Team Practice",
  "team_bonding": "Team Bonding",
  "fundraiser": "Fundraiser",
  "meeting": "Team Meeting",
  "parent_meeting": "Parent Meeting",
  "competition": "Competition",
  "class": "Class",
  "private_lesson": "Private Lesson",
  "tryout": "Tryout / Evaluation",
  "other": "Other Event"
};

// =============================================================================
// EVENT TYPE HELPER FUNCTIONS
// =============================================================================

/**
 * Get user-friendly label for event types
 * 
 * @param eventType - The event type value (e.g., 'practice', 'meeting')
 * @returns The display label (e.g., 'Team Practice', 'Team Meeting')
 */
export const getEventTypeLabel = (eventType: string): string => {
  return EVENT_TYPE_NAMES[eventType] || eventType.charAt(0).toUpperCase() + eventType.slice(1);
};

/**
 * UNIFIED EVENT-TYPE PALETTE (web + mobile parity)
 *
 * Six buckets, one color per bucket. Same mapping is mirrored in
 * cheervue-mobile-app/utils/constants/eventColors.ts so the calendar pill
 * on web matches the event card on mobile.
 *
 *   Team (brand pink)  — practice, tryout, team_bonding, meeting, parent_meeting
 *   Class (purple)     — class, camp, workshop, clinic
 *   Private (blue)     — private_lesson
 *   Competition (green)— competition, comp
 *   Fundraiser (orange)— fundraiser  (split off from Other on 2026-05-16 so
 *                        donation drives, raffles, and sponsorship asks stay
 *                        visually loud and gyms remember to promote them)
 *   Other (gray)       — other / unknown
 */

/**
 * Get badge color for event types — used by Badge components in lists/cards.
 * Tokens map to:  primary=brand pink, purple, blue, success=green, orange,
 * light=gray.
 */
export const getEventTypeBadge = (eventType?: string): BadgeColor => {
  switch (eventType) {
    // Team
    case 'practice':
    case 'tryout':
    case 'team_bonding':
    case 'meeting':
    case 'team_meeting':
    case 'parent_meeting':
      return 'primary';        // Brand pink
    // Class
    case 'class':
    case 'camp':
    case 'workshop':
    case 'clinic':
      return 'purple';
    // Private Lesson
    case 'private_lesson':
      return 'blue';
    // Competition
    case 'competition':
    case 'comp':
      return 'success';        // Green
    // Fundraiser
    case 'fundraiser':
      return 'orange';
    // Other / unknown
    case 'other':
    default:
      return 'light';          // Gray
  }
};

/**
 * Get the icon + Tailwind color trio for an event type — used by dashboard
 * cards that render an icon-with-colored-bg rather than a Badge component.
 * Returns the canonical icon name plus 100/600 (light) and 900/30/400 (dark)
 * surface classes for the unified palette.
 */
export const getEventTypeIconData = (
  eventType?: string,
): { name: string; color: string; bg: string } => {
  switch (eventType) {
    case 'competition':
      return { name: 'emoji_events', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-900/30' };
    case 'practice':
    case 'tryout':
    case 'team_bonding':
    case 'meeting':
    case 'parent_meeting':
      return { name: 'calendar_today', color: 'text-brand-600 dark:text-brand-400', bg: 'bg-brand-100 dark:bg-brand-900/30' };
    case 'class':
    case 'camp':
      return { name: 'school', color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-100 dark:bg-purple-900/30' };
    case 'private_lesson':
      return { name: 'person', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30' };
    case 'fundraiser':
      return { name: 'volunteer_activism', color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-100 dark:bg-orange-900/30' };
    default:
      return { name: 'event', color: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-900/30' };
  }
};

/**
 * Get calendar color for event types — used by FullCalendar event styling.
 * Tokens map to fc-bg-* classes in globals.css.
 */
export const getEventTypeColor = (eventType?: string): string => {
  switch (eventType) {
    // Team
    case 'practice':
    case 'tryout':
    case 'team_bonding':
    case 'meeting':
    case 'team_meeting':
    case 'parent_meeting':
      return 'Primary';        // Brand pink (fc-bg-primary)
    // Class
    case 'class':
    case 'camp':
    case 'workshop':
    case 'clinic':
      return 'Purple';         // fc-bg-purple
    // Private Lesson
    case 'private_lesson':
      return 'Blue';           // fc-bg-blue (added 2026-05-15)
    // Competition
    case 'competition':
    case 'comp':
      return 'Success';        // Green (fc-bg-success)
    // Fundraiser
    case 'fundraiser':
      return 'Orange';         // fc-bg-orange (added 2026-05-16)
    // Gym Closings V1 (U4) — when an "event-shaped" token surfaces a closed
    // day on a list view, the gym_closed bucket paints the neutral-gray
    // pill. Day-cell painting on the calendar grid uses the same fc-bg-
    // gym-closed class via dayCellClassNames, not via this map.
    //
    // IMPORTANT: this returns the literal token `'gym-closed'` (NOT
    // `'GymClosed'`). calendarHelpers.tsx builds the chip class as
    // `fc-bg-${calendar.toLowerCase()}`, so we must return a string whose
    // lowercased form exactly matches the CSS class in globals.css
    // (`.fc-bg-gym-closed`). U5 mobile palette lockstep + U10 closing-
    // message rendering both rely on this contract — regression covered
    // by the test in `__tests__/eventConstants.test.ts`.
    case 'gym_closed':
      return 'gym-closed';     // fc-bg-gym-closed (lowercases cleanly)
    // Other / unknown
    case 'other':
    default:
      return 'Gray';           // fc-bg-gray
  }
};

// =============================================================================
// RECURRING-SERIES EXCEPTION MARKERS
// =============================================================================

/**
 * Description markers that flag an event row as a single-occurrence exception
 * to a recurring series. The windowed calendar fetch
 * (eventService.getEventsForCalendarWindow) and the client-side expander
 * (recurringEvents.ts) MUST match on the same literals — if they drift, the
 * fetch silently strands exception occurrences outside the visible window
 * (issue #1840). Author and match markers only via these exports.
 */
export const EXCEPTION_MARKER = '[EXCEPTION]';
export const EDITED_FROM_SERIES_PREFIX = '[EDITED_FROM_SERIES:';

/**
 * Builds the hidden HTML-comment marker appended to an exception row's
 * description. `occurrenceKey` is either the occurrence index (dashboard
 * single-edit) or a YYYY-MM-DD date key (assistant single-edit).
 */
export const buildEditedFromSeriesMarker = (
  parentEventId: string,
  occurrenceKey: string | number,
): string => `<!-- ${EDITED_FROM_SERIES_PREFIX}${parentEventId}:${occurrenceKey}] -->`;

/**
 * PostgREST `.or()` filter matching any row carrying an exception marker
 * (either format) in its description.
 */
export const buildExceptionMarkerOrFilter = (): string =>
  `description.ilike.*${EDITED_FROM_SERIES_PREFIX}*,description.ilike.*${EXCEPTION_MARKER}*`;

// =============================================================================
// RECURRENCE OPTIONS
// =============================================================================

/**
 * Recurrence pattern options for recurring events
 */
export const RECURRENCE_PATTERNS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" }
] as const;

/**
 * Days of week for recurrence settings
 */
export const DAYS_OF_WEEK = [
  { value: "SU", label: "Sunday" },
  { value: "MO", label: "Monday" },
  { value: "TU", label: "Tuesday" },
  { value: "WE", label: "Wednesday" },
  { value: "TH", label: "Thursday" },
  { value: "FR", label: "Friday" },
  { value: "SA", label: "Saturday" }
] as const; 